import debounce from 'lodash/debounce'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetEquipmentsWithLocalityFilter } from 'src/api/main-component'
import { IMoveReportDestinationPickerProps, IMoveReportTarget } from 'src/generated-types/form-schema'
import { formatItemIdentification, getCageGenericIdentification } from 'src/utils/function'
import { getTranslatedText } from 'src/utils/translations'
import { MoveReportTargetList } from './MoveReportTargetList'

const EQUIPMENT_PAGE_SIZE = 50

export const MoveReportEquipmentPicker: React.FC<IMoveReportDestinationPickerProps> = ({
  customerId,
  excludedId,
  localityId,
  selected,
  onSelect,
}) => {
  const { t } = useTranslation(['form-schema-report'])
  const [searchText, setSearchText] = useState('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), [])

  const { data, isLoading } = useGetEquipmentsWithLocalityFilter(customerId, {
    search: search || undefined,
    localityId,
    excludeId: excludedId,
    page: 0,
    pageSize: EQUIPMENT_PAGE_SIZE,
  })

  const targets = useMemo<IMoveReportTarget[]>(
    () =>
      (data?.items ?? []).map(destination => ({
        id: destination.id,
        label: formatItemIdentification({
          itemType: getTranslatedText(destination.typeName),
          serialNumber: destination.serialNumber,
        }),
        secondaryLabel: [destination.localityName, getCageGenericIdentification(destination.positionName)]
          .filter(Boolean)
          .join(' · '),
      })),
    [data],
  )

  return (
    <MoveReportTargetList
      searchText={searchText}
      onSearchTextChange={value => {
        setSearchText(value)
        debouncedSearch(value)
      }}
      searchPlaceholder={t('search-equipment', { ns: 'form-schema-report' })}
      targets={targets}
      isLoading={isLoading}
      emptyText={t('no-equipment-found', { ns: 'form-schema-report' })}
      selected={selected}
      onSelect={onSelect}
      loadedCount={targets.length}
      totalCount={data?.totalCount ?? 0}
    />
  )
}
